import * as React from "react";
import { useForm, useFieldArray, useWatch, Control } from "react-hook-form";
import {
  Button,
  Grid,
  Title,
  ActionIcon,
  Group,
  Text,
  Flex,
  Stack,
  Divider,
  useMantineTheme,
} from "@mantine/core";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { IconChevronLeft, IconPlus, IconTrash } from "@tabler/icons-react";

import {
  CSelect,
  CTextarea,
  CTextInput,
} from "@supportinitiative/react-hook-form-mantine";

import {
  RelationshipType,
  RelationshipSchema,
  DatasetType,
} from "@supportinitiative/common";

import { styles } from "../styles";
import { postRelationship } from "../api";
import { AuditType, IDispatchAction } from "../types";
import {
  getColumnsLOV,
  getActionOptions,
  operators,
  getDatasetLOV,
  getDataset,
} from "./RelationshipUtils";
import { UserContext } from "../misc/UserContext";
import { useDatasets, useRelationship } from "../hooks";
import FormSubmitError from "../ui/Error/FormSubmitError";
import FormStateError from "../ui/Error/FormStateError";
import Success from "../ui/Success";
import Audit from "../ui/Audit/Audit";
import { getId } from "../utilities";

type actionTypes =
  | "CURRENT-ACTION"
  | "ACTION-OPTIONS"
  | "SET-VALUE"
  | "SET-SUBMITTING"
  | "SET-SUCCESS"
  | "SET-ERROR";

export const RelationshipCVMContext = React.createContext<{
  state: any;
  dispatch: React.Dispatch<IDispatchAction<actionTypes>>;
}>({
  state: {},
  dispatch: () => {},
});

const initialState = {
  currentAction: "NEW",
  actionOptions: {},
  setValue: () => {},
  submitting: false,
  error: "",
  successMessage: "",
};

function reducer(state: any, action: IDispatchAction<actionTypes>) {
  switch (action.type) {
    case "CURRENT-ACTION":
      return { ...state, currentAction: action.value };
    case "ACTION-OPTIONS":
      return { ...state, actionOptions: action.value };
    case "SET-VALUE":
      return { ...state, setValue: action.value };
    case "SET-SUBMITTING":
      return { ...state, submitting: action.value };
    case "SET-SUCCESS":
      return { ...state, successMessage: action.value };
    case "SET-ERROR":
      return { ...state, error: action.value };
    default:
      return state;
  }
}

function Links({ control }: { control: Control<RelationshipType> }) {
  const linksArray = useFieldArray({
    control,
    name: "definition.links",
    keyName: "__key",
  });

  const { state: relationshipFormState } = React.useContext(
    RelationshipCVMContext
  );

  return (
    <Stack gap="0" mb="lg">
      <Group align="center" style={{ alignItems: "center" }}>
        <Flex>
          <Title
            mb="xs"
            mt="xs"
            order={5}
            style={{
              padding: "0px 5px",
              backgroundColor: "#16A596",
              color: "#fff",
            }}
          >
            Links
          </Title>
        </Flex>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={() => {
            linksArray.append({
              id: getId(),
              description: "",
              definition: "",
            });
          }}
          style={{
            display:
              relationshipFormState.currentAction.toUpperCase() === "VIEW"
                ? "none"
                : "",
          }}
          disabled={relationshipFormState.actionOptions.disableAll}
        >
          <IconPlus />
        </ActionIcon>
      </Group>

      {linksArray.fields.length === 0 ? (
        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={8}>
            <Divider label="No links" labelPosition="left" />
          </Grid.Col>
        </Grid>
      ) : (
        <Stack gap="0" mb="lg">
          {linksArray.fields.map((field, index) => {
            return (
              <Grid align="stretch" gutter={"xs"} mb="xs" key={field.id}>
                <Grid.Col span={5}>
                  <CTextarea
                    name={`definition.links.${index}.description`}
                    control={control}
                    label="Description"
                    autosize
                    minRows={2}
                    maxRows={20}
                    required
                    showErrorText={false}
                    disabled={relationshipFormState.actionOptions.disableAll}
                  />
                </Grid.Col>
                <Grid.Col span={5}>
                  <CTextarea
                    name={`definition.links.${index}.definition`}
                    control={control}
                    label="Definition"
                    autosize
                    minRows={2}
                    maxRows={20}
                    required
                    showErrorText={false}
                    disabled={relationshipFormState.actionOptions.disableAll}
                  />
                </Grid.Col>
                <Grid.Col
                  span={1}
                  style={{ display: "flex", alignItems: "flex-end" }}
                >
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={() => {
                      linksArray.remove(index);
                    }}
                    style={{
                      display:
                        relationshipFormState.currentAction.toUpperCase() ===
                        "VIEW"
                          ? "none"
                          : "",
                    }}
                    disabled={relationshipFormState.actionOptions.disableAll}
                  >
                    <IconTrash />
                  </ActionIcon>
                </Grid.Col>
              </Grid>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

export default function RelationshipCVM() {
  // let [relationship, setRelationship] = React.useState<RelationshipType>();

  let [audit, setAudit] = React.useState<AuditType | undefined>();

  // let [currentAction, setCurrentAction] = React.useState("new");
  let navigate = useNavigate();
  let [searchParams] = useSearchParams();
  let id = searchParams.get("id"),
    entityId = searchParams.get("entityId");
  const { state: applicationState } = React.useContext(UserContext);
  const queryClient = useQueryClient();
  const theme = useMantineTheme();

  const { data: datasets } = useDatasets(entityId);

  const { data: relationship } = useRelationship(entityId, id);
  // let [newRelationship, setNewRelationship] = React.useState<RelationshipType | undefined>();
  const [relationshipFormState, relationshipFormDispatch] = React.useReducer(
    reducer,
    {
      ...initialState,
      actionOptions: getActionOptions(
        "new",
        relationship,
        relationship,
        applicationState
      ),
    }
  );
  // console.log("relationship in component", relationship);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<RelationshipType>({
    defaultValues: {
      primaryDataset: "",
      secondaryDataset: "",
      displayName: "",
      description: "",
      definition: {
        links: [],
      },
    },
    criteriaMode: "all",
    resolver: yupResolver(RelationshipSchema),
  });

  const linksArray = useFieldArray({
    control,
    name: "definition.links",
    keyName: "__key",
  });
  /* 
  const columns = useWatch({
    control,
    name: "definition.columns",
  }); */

  // const [columns, setColumns] = React.useState<RelationshipType["definition"]["columns"]>([]);

  const onOk = () => {
    navigate(`/app/relationships?entityId=${entityId}`);
  };

  React.useEffect(() => {
    if (relationship) {
      relationshipFormDispatch({ type: "CURRENT-ACTION", value: "VIEW" });
      relationshipFormDispatch({
        type: "ACTION-OPTIONS",
        value: getActionOptions(
          "view",
          relationship,
          relationship,
          applicationState
        ),
      });

      // setColumns(relationship.definition.columns || []);

      setAudit({
        status: relationship.status,
        authStatus: relationship.authStatus,
        usableStatus: relationship.usableStatus,
        makerId: relationship.makerId,
        makerDate: relationship.makerDate,
        modificationNo: relationship.modificationNo,
        checkerId: relationship.checkerId,
        checkerDate: relationship.checkerDate,
      });

      reset(relationship);
    }
  }, [relationship]);

  const onSubmit = async (data: any) => {
    try {
      relationshipFormDispatch({
        type: "SET-SUBMITTING",
        value: true,
      });
      relationshipFormDispatch({
        type: "SET-ERROR",
        value: "",
      });
      let payload = {
        ...data,
        entityId,
        metaData: {
          action: relationshipFormState.currentAction,
        },
      };
      await postRelationship(payload);
      // setError("Form", "Unable to Submit");
      relationshipFormDispatch({
        type: "SET-SUBMITTING",
        value: false,
      });
      queryClient.invalidateQueries(["relationship", entityId, id]);
      queryClient.invalidateQueries(["relationships", entityId]);
      relationshipFormDispatch({
        type: "SET-SUCCESS",
        value: "Relationship Saved Successfully",
      });
    } catch (error: any) {
      // console.log("post catch", error);
      // console.log("post catch", error?.response?.data?.message);
      relationshipFormDispatch({
        type: "SET-SUBMITTING",
        value: false,
      });
      relationshipFormDispatch({
        type: "SET-ERROR",
        value: error?.response?.data?.message || "Unable to save relationship",
      });
    }
  };

  return (
    <RelationshipCVMContext.Provider
      value={{
        state: relationshipFormState,
        dispatch: relationshipFormDispatch,
      }}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ marginTop: theme.spacing.lg }}
        noValidate
      >
        <div id="header">
          <Grid
            align="flex-end"
            gutter={"xs"}
            mt="xs"
            mb="xs"
            // mb="xs"
            style={{
              display: "flex",
              // borderLeft: `${theme.spacing.xxs} solid ${theme.colors.emerald[7]}`,
            }}
            columns={12}
          >
            <Grid.Col span={3}>
              <Flex align={"end"} gap={theme.spacing.xs}>
                <Stack gap={0}>
                  <Title order={3} c="dark">
                    Relationship
                  </Title>
                </Stack>
              </Flex>
            </Grid.Col>
          </Grid>
        </div>

        <Divider size="lg" mb="xs" color="dark.2" />

        <div id="body">
          <Grid align="flex-start" gutter={"xs"} mb="lg">
            <Grid.Col span={4}>
              <CSelect
                name="primaryDataset"
                control={control}
                label={"Primary Dataset"}
                data={getDatasetLOV(datasets)}
                showErrorText={false}
                searchable
                required
                disabled={relationshipFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <CSelect
                name="secondaryDataset"
                control={control}
                label={"Secondary Dataset"}
                data={getDatasetLOV(datasets)}
                showErrorText={false}
                searchable
                required
                disabled={relationshipFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>

          <Grid
            align="flex-start"
            gutter={"xs"}
            mb="xs"
            style={{ display: "flex", alignItems: "flex-end" }}
          >
            <Grid.Col span={8}>
              <CTextarea
                name="description"
                control={control}
                label="Description"
                autosize
                minRows={2}
                maxRows={20}
                required
                showErrorText={false}
                disabled={relationshipFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>

          <Links control={control} />
        </div>

        {(Object.keys(errors).length > 0 || relationshipFormState.error) && (
          <div id="errors">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <FormStateError errors={errors} />
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <FormSubmitError error={relationshipFormState.error} />
            </div>
          </div>
        )}

        <Grid align="stretch" gutter={"xs"} columns={24} mb={"lg"}>
          <>
            {(relationshipFormState.actionOptions.allowedActions || []).map(
              (action: string) => {
                if (action === "save") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        fullWidth
                        type="submit"
                        color="teal"
                        aria-label="Save"
                        loading={relationshipFormState.submitting}
                      >
                        Save
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "cancel") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        fullWidth
                        component={Link}
                        to={`/app/relationships?entityId=${entityId}`}
                        aria-label="Cancel"
                      >
                        Cancel
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "back") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        fullWidth
                        component={Link}
                        to={`/app/relationships?entityId=${entityId}`}
                        aria-label="Back"
                      >
                        Back
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "edit") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        aria-label="Edit"
                        fullWidth
                        onClick={() => {
                          relationshipFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "EDIT",
                          });
                          relationshipFormDispatch({
                            type: "ACTION-OPTIONS",
                            value: getActionOptions(
                              "edit",
                              relationship,
                              relationship,
                              applicationState
                            ),
                          });

                          setAudit({
                            status: relationship?.status,
                            authStatus: "UNAUTH",
                            usableStatus: relationship?.usableStatus,
                            makerId: applicationState.user.userId,
                            makerDate: new Date(),
                            modificationNo:
                              relationship?.authStatus === "UNAUTH"
                                ? relationship?.modificationNo
                                : (relationship?.modificationNo || 0) + 1,
                            checkerId: "",
                            checkerDate: undefined,
                          });
                        }}
                      >
                        Edit
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "auth") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        aria-label="Auth"
                        fullWidth
                        onClick={async () => {
                          relationshipFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "AUTH",
                          });

                          try {
                            let payload = {
                              ...relationship,
                              entityId,
                              metaData: {
                                action: "auth",
                              },
                            };
                            await postRelationship(payload);
                            // setError("Form", "Unable to Submit");
                            relationshipFormDispatch({
                              type: "CURRENT-ACTION",
                              value: "VIEW",
                            });

                            relationshipFormDispatch({
                              type: "SET-SUCCESS",
                              value: "Relationship Authorized Successfully",
                            });
                          } catch (error: any) {
                            // console.log("post catch", error);
                            relationshipFormDispatch({
                              type: "SET-ERROR",
                              value:
                                error?.response?.data?.message ||
                                "Unable to Authorize Relationship",
                            });
                          }
                        }}
                      >
                        Auth
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "delete") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        aria-label="Delete"
                        fullWidth
                        onClick={async () => {
                          relationshipFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "DELETE",
                          });

                          try {
                            let payload = {
                              ...relationship,
                              entityId,
                              metaData: {
                                action: "delete",
                              },
                            };
                            await postRelationship(payload);
                            // setError("Form", "Unable to Submit");
                            relationshipFormDispatch({
                              type: "CURRENT-ACTION",
                              value: "VIEW",
                            });

                            relationshipFormDispatch({
                              type: "SET-SUCCESS",
                              value: "Relationship Deleted Successfully",
                            });
                          } catch (error: any) {
                            // console.log("post catch", error);
                            relationshipFormDispatch({
                              type: "SET-ERROR",
                              value:
                                error?.response?.data?.message ||
                                "Unable to Delete Relationship",
                            });
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </Grid.Col>
                  );
                } else {
                  return null;
                }
              }
            )}
          </>
        </Grid>

        <Audit audit={audit} />
        {relationshipFormState.successMessage && (
          <Success message={relationshipFormState.successMessage} onOk={onOk} />
        )}
      </form>
    </RelationshipCVMContext.Provider>
  );
}
